// pages/findings/[domain]/[year]/[slug].js
// ════════════════════════════════════════════════════════════════════
// LAB FINDING — bare-slug route (Phase F1b + F6)
//
// Resolves to the LATEST version of a finding. The finding's actual
// rendering happens through the registry component, which handles its
// own version display via <VersionStatus>.
//
// Versioned canonical URLs live at the sibling [version] route.
// ════════════════════════════════════════════════════════════════════

import Head from 'next/head';
import { FINDING_REGISTRY, getFinding } from '../../../../features/findings/registry';

export default function FindingBareSlugPage({ findingKey }) {
  const entry = findingKey ? FINDING_REGISTRY[findingKey] : null;

  if (!entry) return null;

  const Component = entry.component;
  const { meta } = entry;

  return (
    <div className="sp-root">
      <Head>
        <title>{meta.pageTitle}</title>
        {meta.description && <meta name="description" content={meta.description} />}
        {/* Canonical points at the versioned URL for citation stability */}
        <link
          rel="canonical"
          href={`/findings/${meta.domain}/${meta.year}/${meta.slug}/${meta.version}`}
        />
      </Head>
      <Component />
    </div>
  );
}

export async function getStaticPaths() {
  // Generate one bare-slug page per unique (domain, year, slug) — the
  // route resolves to the latest version of that slug.
  const seen = new Set();
  const paths = [];
  for (const key of Object.keys(FINDING_REGISTRY)) {
    const [domain, year, slug] = key.split('/');
    const slugKey = `${domain}/${year}/${slug}`;
    if (seen.has(slugKey)) continue;
    seen.add(slugKey);
    paths.push({ params: { domain, year, slug } });
  }
  return { paths, fallback: false };
}

export async function getStaticProps({ params }) {
  const { domain, year, slug } = params;
  const entry = getFinding(domain, year, slug);
  if (!entry) return { notFound: true };
  const findingKey = `${domain}/${year}/${slug}/${entry.meta.version}`;
  return { props: { findingKey } };
}
